// Canonical wizard data shape. The seed adapter transforms the bundled JSON
// into this shape on first load; the save path writes it verbatim back to AppConfig.

export type MedicationKind = 'drug' | 'drip';
export type DrugType = null | 'mass' | 'fluid';
export type DripCalcType = null | 'DilutionInstructions' | 'InfusionSpeed';
export type AgeUnit = 'days' | 'months' | 'years';

export interface CasePredicate {
  type: 'weight' | 'age';
  minWeight?: number | null;
  maxWeight?: number | null;
  minAge?: number | null;
  maxAge?: number | null;
  unit?: AgeUnit;
}

export interface CaseDetails {
  // Any subset of the medication detail fields can override per-case.
  dose_per_kg?: number | null;
  dose_unit?: string | null;
  constant_dose?: number | null;
  concentration?: string | null;
  maxDose?: string | null;
  minDose?: string | null;
  minDoseUnit?: string | null;
  dose_range?: string | null;
  comment?: string | null;
  shouldDispalyConcentration?: boolean | null;
  type?: DrugType;
  warnText?: string | null;
  administrationInstructions?: string | null;
  // Drip-side overrides
  dose_per_kg_per_min?: number | null;
  dose_per_kg_per_hour?: number | null;
  default_dilution_volume_ml?: number | null;
  default_dilution_volume_unit?: string | null;
  target_volume_ml_per_hour?: number | null;
  existing_dilution_concentration?: string | null;
  existing_dilution_concentration_dose_unit?: string | null;
  max_dose_per_hour?: number | null;
  allowed_dose_range?: string | null;
}

export interface MedicationCase {
  predicate: CasePredicate;
  details: CaseDetails;
}

export interface Attachment {
  name: string;
  file: string; // relative path in Azure (e.g. "apps/resus/emek/foo.pdf") OR a legacy bare filename
}

export interface DefiEntry {
  name: string;
  joulePerKg: number;
}

export interface ProtocolRecord {
  id: string;
  slug?: string;
  name: string;
  section: string;
  attachments: Attachment[];
  hidden?: boolean;
}

export interface DrugRecord {
  id: string;
  slug?: string;
  kind: 'drug';
  name: string;
  howToGive?: string | null;
  comment?: string | null;
  warnText?: string | null;
  administrationInstructions?: string | null;
  type: DrugType;
  dose_per_kg?: number | null;
  dose_unit?: string | null;
  dose_range?: string | null;
  concentration?: string | null;
  maxDose?: string | null;
  minDose?: string | null;
  minDoseUnit?: string | null;
  constant_dose?: number | null;
  shouldDispalyConcentration?: boolean | null;
  cases?: MedicationCase[] | null;
}

export interface DripRecord {
  id: string;
  slug?: string;
  kind: 'drip';
  name: string;
  howToGive?: string | null;
  comment?: string | null;
  warnText?: string | null;
  administrationInstructions?: string | null;
  calc_type: DripCalcType;
  dose_unit?: string | null;
  allowed_dose_range?: string | null;
  dose_per_kg_per_min?: number | null;
  dose_per_kg_per_hour?: number | null;
  default_dilution_volume_ml?: number | null;
  default_dilution_volume_unit?: string | null;
  target_volume_ml_per_hour?: number | null;
  existing_dilution_concentration?: string | null;
  existing_dilution_concentration_dose_unit?: string | null;
  max_dose_per_hour?: number | null;
  cases?: MedicationCase[] | null;
}

export type MedicationRecord = DrugRecord | DripRecord;

export interface AssignmentRecord {
  protocolId: string;
  medicationId: string;
  order: number;
  comment: string;
}

export interface DrugSectionRecord {
  name: string;
  medicationIds: string[];
}

export interface ResusConfigData {
  schemaVersion: 1;
  protocols: ProtocolRecord[];
  medications: MedicationRecord[];
  assignments: AssignmentRecord[];
  drugSections: DrugSectionRecord[];
  defibrillation: Record<string, DefiEntry[]>;
}

export interface FieldError {
  field: string;
  message: string;
}

export type WizardStep = 1 | 2 | 3;

export interface WizardState {
  data: ResusConfigData;
  ui: {
    selectedProtocolId: string | null;
    selectedMedicationId: string | null;
    currentStep: WizardStep;
    dirty: boolean;
    errors: FieldError[];
    sampleWeightPreset: 'newborn' | 'child';
    protocolSearch: string;
    medicationSearch: string;
  };
}

export const SAMPLE_PRESETS = {
  newborn: { label: 'תינוק (3 ק"ג)', weight: 3, ageMonths: 0 },
  child: { label: 'ילד (20 ק"ג)', weight: 20, ageMonths: 60 }
} as const;

export type SamplePresetKey = keyof typeof SAMPLE_PRESETS;
